import { useMemo } from 'react';

import { QueryResult, gql } from '@apollo/client';

import {
  ChatMentionTokensQuery,
  RenderType,
  TemplateToken,
  useChatMentionTokensQuery,
} from 'src/generated/mloop-graphql';

import { EMPTY_OBJECT } from 'src/constants';

import placeholders from './utils/placeholders';

/**
 * TOKENS TO DISPLAY
 * Note Only add if eligible for display
 * */
export enum Tokens {
  CHAT_MENTION_INTERVIEWERS = 'CHAT_MENTION_INTERVIEWERS',
  CHAT_MENTION_CANDIDATE_RECRUITER = 'CHAT_MENTION_CANDIDATE_RECRUITER',
  CHAT_MENTION_CANDIDATE_COORDINATOR = 'CHAT_MENTION_CANDIDATE_COORDINATOR',
  CHAT_MENTION_CANDIDATE_SOURCER = 'CHAT_MENTION_CANDIDATE_SOURCER',
  CHAT_MENTION_JOB_HIRING_MANAGERS = 'CHAT_MENTION_JOB_HIRING_MANAGERS',
  CHAT_MENTION_DEBRIEF_ATTENDEES = 'CHAT_MENTION_DEBRIEF_ATTENDEES',
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ChatMentionTemplateTokenQuery = gql`
  query ChatMentionTokens($id: String!, $applicationId: uuid!, $interviewerIds: [uuid!]!, $renderType: RenderType!) {
    CHAT_MENTION_INTERVIEWERS: templateToken(input: { id: $id, renderType: $renderType }) {
      id
      CHAT_MENTION_INTERVIEWERS(input: { interviewerIds: $interviewerIds })
    }
    CHAT_MENTION_DEBRIEF_ATTENDEES: templateToken(input: { id: $id, renderType: $renderType }) {
      id
      CHAT_MENTION_DEBRIEF_ATTENDEES(input: { interviewerIds: $interviewerIds })
    }
    CHAT_MENTION_CANDIDATE_SOURCER: templateToken(input: { id: $id, renderType: $renderType }) {
      id
      CHAT_MENTION_CANDIDATE_SOURCER(input: { id: $applicationId })
    }
    CHAT_MENTION_CANDIDATE_RECRUITER: templateToken(input: { id: $id, renderType: $renderType }) {
      id
      CHAT_MENTION_CANDIDATE_RECRUITER(input: { id: $applicationId })
    }
    CHAT_MENTION_CANDIDATE_COORDINATOR: templateToken(input: { id: $id, renderType: $renderType }) {
      id
      CHAT_MENTION_CANDIDATE_COORDINATOR(input: { id: $applicationId })
    }
    CHAT_MENTION_JOB_HIRING_MANAGERS: templateToken(input: { id: $id, renderType: $renderType }) {
      id
      CHAT_MENTION_JOB_HIRING_MANAGERS(input: { id: $applicationId })
    }
  }
`;

export type ChatMentionPlaceholderFillerOptions = Pick<TemplateToken, keyof typeof Tokens>;

export default class ChatMentionPlaceholderFiller {
  static getFilledText = (input: string, options: ChatMentionPlaceholderFillerOptions): string => {
    return placeholders(input, options);
  };

  static getOptions = (data?: ChatMentionTokensQuery): ChatMentionPlaceholderFillerOptions => {
    if (!data || !Object.keys(data).length) {
      return EMPTY_OBJECT;
    }
    return {
      CHAT_MENTION_INTERVIEWERS: data.CHAT_MENTION_INTERVIEWERS?.CHAT_MENTION_INTERVIEWERS,
      CHAT_MENTION_DEBRIEF_ATTENDEES: data.CHAT_MENTION_DEBRIEF_ATTENDEES?.CHAT_MENTION_DEBRIEF_ATTENDEES,
      CHAT_MENTION_CANDIDATE_SOURCER: data.CHAT_MENTION_CANDIDATE_SOURCER?.CHAT_MENTION_CANDIDATE_SOURCER,
      CHAT_MENTION_CANDIDATE_RECRUITER: data.CHAT_MENTION_CANDIDATE_RECRUITER?.CHAT_MENTION_CANDIDATE_RECRUITER,
      CHAT_MENTION_CANDIDATE_COORDINATOR: data.CHAT_MENTION_CANDIDATE_COORDINATOR?.CHAT_MENTION_CANDIDATE_COORDINATOR,
      CHAT_MENTION_JOB_HIRING_MANAGERS: data.CHAT_MENTION_JOB_HIRING_MANAGERS?.CHAT_MENTION_JOB_HIRING_MANAGERS,
    };
  };
}

type Input = {
  renderType: RenderType;
  applicationId: string;
  interviewerIds: string[];
  isFetch?: boolean;
};

export const useChatMentionPlaceholderFillerOptions = ({
  renderType,
  applicationId,
  interviewerIds,
  isFetch = true,
}: Input): [ChatMentionPlaceholderFillerOptions, Pick<QueryResult, 'data' | 'error' | 'loading'>] => {
  const { data, loading, error } = useChatMentionTokensQuery({
    skip: !applicationId || !isFetch,
    variables: {
      id: applicationId,
      applicationId,
      interviewerIds,
      renderType: renderType || RenderType.Html,
    },
    errorPolicy: 'all',
  });

  return useMemo(() => {
    return [ChatMentionPlaceholderFiller.getOptions(data), { data, error, loading }];
  }, [data, error, loading]);
};
